import React, { useRef, useState, useEffect, useCallback } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

const SCROLL_AMOUNT = 240;

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    position: 'relative',
  },
  overlay: {
    position: 'absolute',
    width: 64,
    display: 'flex',
    alignItems: 'center',
    zIndex: 1,
    pointerEvents: 'all',
    cursor: 'pointer',
    borderRadius: '5px 5px',
  },
  overlayLeft: {
    left: 0,
    background: 'linear-gradient(to right, rgba(234, 234, 234, 0.92) 35%, transparent)',
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(0.5),
  },
  overlayRight: {
    right: 0,
    background: 'linear-gradient(to left, rgba(234, 234, 234, 0.92) 35%, transparent)',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(0.5),
  },
  arrowButton: {
    padding: theme.spacing(0.5),
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

interface Props {
  children: React.ReactNode;
  className?: string;
}

export default function HorizontalScrollContainer({ children, className }: Props) {
  const classes = useStyles();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [overlayInset, setOverlayInset] = useState({ top: 0, bottom: 0 });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const style = getComputedStyle(el);
    setOverlayInset({
      top: Math.max(0, parseFloat(style.paddingTop) - 1),                             
      bottom: Math.max(0, parseFloat(style.paddingBottom) - 2),  
    });
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  useEffect(() => {
    updateScrollState();
  });

  const handleScrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  };

  const overlayStyle = { top: overlayInset.top, bottom: overlayInset.bottom };

  return (
    <div className={classes.wrapper}>
      <div ref={scrollRef} className={className}>
        {children}
      </div>
      {canScrollLeft && (
        <div className={`${classes.overlay} ${classes.overlayLeft}`} style={overlayStyle} onClick={handleScrollLeft}>
          <IconButton className={classes.arrowButton} size="small">
            <ArrowBackIosIcon fontSize="small" style={{ color: 'rgba(175, 173, 173, 0.76)' }} />
          </IconButton>
        </div>
      )}
      {canScrollRight && (
        <div className={`${classes.overlay} ${classes.overlayRight}`} style={overlayStyle} onClick={handleScrollRight}>
          <IconButton className={classes.arrowButton} size="small">
            <ArrowForwardIosIcon fontSize="small" style={{ color: 'rgba(175, 173, 173, 0.76)' }} />
          </IconButton>
        </div>
      )}
    </div>
  );
}
